import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  IconButton,
  useMediaQuery,
  useTheme,
  Box,
  Typography,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  InputAdornment,
  Divider,
  Chip,
} from '@mui/material';
import {
  Close as CloseIcon,
  Class as ClassIcon,
  Person as PersonIcon,
  PersonOff as PersonOffIcon,
  Email as EmailIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { SelectChangeEvent } from '@mui/material';

interface School {
  id: number;
  name: string;
}

interface Teacher {
  id: number;
  name: string;
  email: string;
}

interface GradeFormData {
  name: string;
  school_id: number;
  teacher_id: number;
}

export interface GradeFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (formData: GradeFormData) => Promise<void>;
  formData: GradeFormData;
  onFormChange: (field: string, value: string | number) => void;
  schools: School[];
  teachers: Teacher[];
  assignedTeacherIds?: number[];
  isSaving: boolean;
}

const GradeFormModal: React.FC<GradeFormModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  formData,
  onFormChange,
  schools,
  teachers,
  assignedTeacherIds = [],
  isSaving,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [teacherSearch, setTeacherSearch] = useState('');

  // איפוס שדה החיפוש בכל פתיחה/סגירה של המודאל
  useEffect(() => {
    if (!isOpen) setTeacherSearch('');
  }, [isOpen]);

  const handleSubmit = async () => {
    await onSubmit(formData);
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    onFormChange(name, value);
  };

  const handleSelectChange = (event: SelectChangeEvent<number>) => {
    const { name, value } = event.target;
    onFormChange(name as string, value);
  };

  const availableTeachers = teachers.filter(
    teacher => !assignedTeacherIds.includes(teacher.id)
  );

  // סינון לפי מונח החיפוש (שם או אימייל)
  const searchQuery = teacherSearch.trim().toLowerCase();
  const filteredTeachers = searchQuery
    ? availableTeachers.filter(
        teacher =>
          teacher.name.toLowerCase().includes(searchQuery) ||
          teacher.email.toLowerCase().includes(searchQuery)
      )
    : availableTeachers;

  const isFormValid = formData.name.trim() && formData.school_id;

  return (
    <Dialog
      open={isOpen}
      onClose={isSaving ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      disableEscapeKeyDown={isSaving}
      sx={{
        '& .MuiDialog-paper': {
          borderRadius: isMobile ? 0 : 3,
          position: 'relative',
        }
      }}
    >
      <IconButton
        onClick={onClose}
        disabled={isSaving}
        className="close-button"
        sx={{ color: '#666' }}
      >
        <CloseIcon />
      </IconButton>

      <DialogTitle 
        sx={{ 
          textAlign: 'center',
          pb: 2,
          pt: 3,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 2
        }}
      >
        <div 
          className="modal-icon" 
          style={{
            width: 64,
            height: 64,
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 8px 24px rgba(240, 147, 251, 0.3)',
          }}
        >
          <ClassIcon sx={{ color: 'white', fontSize: 32 }} />
        </div>
        {t('addClass')}
      </DialogTitle>

      <DialogContent sx={{ pt: 1, pb: 2, px: 3 }}>
        <div className="form-container">
          <TextField
            fullWidth
            margin="normal"
            label={t("className")}
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            disabled={isSaving}
            required
            placeholder={t("enterClassName")}
          />

          <FormControl fullWidth margin="normal" disabled={isSaving} required>
            <InputLabel>{t("school")}</InputLabel>
            <Select
              name="school_id"
              value={formData.school_id || ''}
              label={t("school")}
              onChange={handleSelectChange}
            >
              {schools.map((school) => (
                <MenuItem key={school.id} value={school.id}>
                  {school.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth margin="normal" disabled={isSaving}>
            <InputLabel>{t("teacher")} ({t("optional")})</InputLabel>
            <Select
              name="teacher_id"
              value={formData.teacher_id || ''}
              label={`${t("teacher")} (${t("optional")})`}
              onChange={handleSelectChange}
              MenuProps={{
                autoFocus: false,
                PaperProps: {
                  sx: {
                    maxHeight: 320,
                    '& .MuiMenuItem-root': {
                      py: 1.2,
                      px: 2,
                    },
                  },
                },
              }}
              renderValue={(selected) => {
                if (!selected) return t("noTeacher");
                const teacher = availableTeachers.find(t => t.id === selected);
                return teacher ? teacher.name : t("noTeacher");
              }}
            >
              {availableTeachers.length > 0 && (
                <ListSubheader sx={{ p: 1, bgcolor: 'background.paper', lineHeight: 'normal' }}>
                  <TextField
                    size="small"
                    fullWidth
                    autoFocus
                    placeholder={t("searchTeacher")}
                    value={teacherSearch}
                    onChange={(e) => setTeacherSearch(e.target.value)}
                    // מונע מ-Select לחטוף את ההקלדה (type-ahead) ולאבד פוקוס
                    onKeyDown={(e) => {
                      if (e.key !== 'Escape') {
                        e.stopPropagation();
                      }
                    }}
                    onClick={(e) => e.stopPropagation()}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </ListSubheader>
              )}

              <MenuItem value={0}>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <PersonOffIcon fontSize="small" sx={{ color: '#bdbdbd' }} />
                </ListItemIcon>
                <ListItemText
                  primary={t("noTeacher")}
                  primaryTypographyProps={{ color: 'text.secondary', fontSize: '0.9rem' }}
                />
              </MenuItem>

              {availableTeachers.length > 0 && <Divider sx={{ my: 0.5 }} />}

              {availableTeachers.length === 0 && (
                <Box sx={{ px: 2, py: 1.5, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    {t("noAvailableTeachers")}
                  </Typography>
                </Box>
              )}

              {availableTeachers.length > 0 && filteredTeachers.length === 0 && (
                <Box sx={{ px: 2, py: 1.5, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    {t("noMatchingTeachers")}
                  </Typography>
                </Box>
              )}

              {filteredTeachers.map((teacher) => (
                <MenuItem key={teacher.id} value={teacher.id}>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <Box sx={{
                      width: 32,
                      height: 32,
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}>
                      <PersonIcon sx={{ color: 'white', fontSize: 18 }} />
                    </Box>
                  </ListItemIcon>
                  <ListItemText
                    primary={teacher.name}
                    secondary={teacher.email}
                    primaryTypographyProps={{ fontWeight: 500, fontSize: '0.9rem' }}
                    secondaryTypographyProps={{ fontSize: '0.75rem', color: 'text.secondary' }}
                  />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, gap: 1 }} style={{ marginBottom: 0 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          disabled={isSaving}
          sx={{ minWidth: 100 }}
        >
          {t("cancel")}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isSaving || !isFormValid}
          sx={{ minWidth: 100 }}
        >
          {isSaving ? (
            <>
              <CircularProgress size={20} sx={{ mr: 1 }} />
              {t("adding")}
            </>
          ) : (
            t("add")
          )}
        </Button>
      </DialogActions>

      {/* Loading Overlay */}
      {isSaving && (
        <div 
          className="loading-overlay"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000,
            borderRadius: isMobile ? 0 : 12,
          }}
        >
          <CircularProgress 
            size={60}
            sx={{
              color: '#f093fb',
              mb: 2
            }}
          />
          <div 
            className="loading-text"
            style={{
              fontSize: '18px',
              fontWeight: 'bold',
              color: '#2c3e50',
              textAlign: 'center',
              marginBottom: '8px'
            }}
          >
            {t("creatingClass")}
          </div>
          <div 
            style={{
              fontSize: '14px',
              color: '#7f8c8d',
              textAlign: 'center'
            }}
          >
            {t("pleaseWait")}
          </div>
        </div>
      )}
    </Dialog>
  );
};

export default GradeFormModal;